import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { studentLabel } from '../utils/studentLabel';
import {
  WEEKDAY_FULL,
  WEEKDAY_NAMES,
  addDaysISO,
  formatDateShort,
  formatTimeFromISO,
  minutesFromMidnight,
  mondayOf,
  moscowDateISO,
  occurrenceDateMoscow,
  timeFromOccurrenceStart,
  weekDates,
} from '../utils/scheduleDates';

const HOUR_HEIGHT = 48;

function parseHHMM(value, fallback = '11:00') {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return fallback;
  return value.length === 4 ? `0${value}` : value;
}

/** Список учеников с галочками (вместо Ctrl+клик в multi-select). */
function StudentPicker({ students, selectedIds, onChange, max = 4, label = 'Ученики' }) {
  const selected = selectedIds.map(String);
  const atLimit = selected.length >= max;

  function toggle(id) {
    const sid = String(id);
    if (selected.includes(sid)) {
      onChange(selected.filter((x) => x !== sid));
      return;
    }
    if (max === 1) {
      onChange([sid]);
      return;
    }
    if (atLimit) return;
    onChange([...selected, sid]);
  }

  return (
    <fieldset className="sched-student-picker">
      <legend>
        {label}
        <span className="muted">
          {' '}
          ({selected.length}/{max})
        </span>
      </legend>
      <div className="sched-student-picker-list">
        {students.length === 0 ? (
          <p className="muted">Нет активных учеников</p>
        ) : (
          students.map((s) => {
            const sid = String(s.id);
            const checked = selected.includes(sid);
            const disabled = !checked && atLimit && max > 1;
            return (
              <label key={s.id} className={`sched-student-picker-item${disabled ? ' is-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(s.id)}
                />
                <span>{studentLabel(s)}</span>
              </label>
            );
          })
        )}
      </div>
    </fieldset>
  );
}

export default function TeacherSchedulePage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(moscowDateISO()));
  const [settings, setSettings] = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [slots, setSlots] = useState([]);
  const [students, setStudents] = useState([]);
  const [highlightStudentId, setHighlightStudentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [slotFormOpen, setSlotFormOpen] = useState(false);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [slotForm, setSlotForm] = useState({
    weekday: 1,
    start_time: '16:00',
    duration_minutes: 60,
    slot_type: 'individual',
    max_students: 4,
    note: '',
    student_ids: [],
  });

  const [selectedOcc, setSelectedOcc] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [cancelStudentOnly, setCancelStudentOnly] = useState('');

  const [oneoffOpen, setOneoffOpen] = useState(false);
  const [oneoffForm, setOneoffForm] = useState({
    start_local: '',
    duration_minutes: 60,
    slot_type: 'individual',
    student_ids: [],
    note: '',
  });

  const activeStudents = useMemo(
    () => students.filter((s) => !s.relation_status || s.relation_status === 'active'),
    [students]
  );

  const studentsById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(String(s.id), s);
    return map;
  }, [students]);

  function displayNameFor(id, fallback = '') {
    const s = studentsById.get(String(id));
    if (s) return studentLabel(s) || fallback;
    return fallback;
  }

  function occActiveNames(occ) {
    return (occ.students || [])
      .filter((s) => !s.cancelled_for_student)
      .map((s) => displayNameFor(s.id, s.name))
      .filter(Boolean);
  }

  function occTitle(occ) {
    const names = occActiveNames(occ);
    if (occ.is_group) {
      const head = `группа ${occ.enrolled_count}/${occ.max_students}`;
      return names.length ? `${head}: ${names.join(', ')}` : head;
    }
    return names[0] || occ.label || 'слот';
  }
  const [viewMode, setViewMode] = useState('template'); // template | calendar
  const [freeText, setFreeText] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [holidayInput, setHolidayInput] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [holidayHits, setHolidayHits] = useState([]);
  const [examStudentId, setExamStudentId] = useState('');
  const [examDate, setExamDate] = useState('');
  const [examCount, setExamCount] = useState(null);

  const workStart = parseHHMM(settings?.work_start, '11:00');
  const workEnd = parseHHMM(settings?.work_end, '21:00');
  const gridStartMin = minutesFromMidnight(workStart);
  const gridEndMin = Math.max(minutesFromMidnight(workEnd), gridStartMin + 60);
  const gridHours = Math.ceil((gridEndMin - gridStartMin) / 60);
  const days = useMemo(() => weekDates(weekStart), [weekStart]);

  const loadStudents = useCallback(async () => {
    try {
      const res = await api.get('/teacher/students/');
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setStudents(list);
    } catch {
      setStudents([]);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const weekEnd = addDaysISO(weekStart, 6);
      const [calRes, slotsRes, settingsRes, freeRes, holRes] = await Promise.all([
        api.get('/scheduler/teacher/calendar/', {
          params: { from: weekStart, to: weekEnd },
        }),
        api.get('/scheduler/teacher/slots/'),
        api.get('/scheduler/teacher/settings/'),
        api.get('/scheduler/teacher/free-text/'),
        api.get('/scheduler/teacher/holidays/'),
      ]);
      setOccurrences(calRes.data.occurrences || []);
      setSlots(slotsRes.data || []);
      setSettings(settingsRes.data);
      setFreeText(freeRes.data?.text || '');
      setHolidays(holRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить расписание');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    reload();
  }, [reload]);

  const occByDay = useMemo(() => {
    const map = Object.fromEntries(days.map((d) => [d, []]));
    for (const occ of occurrences) {
      const d = occurrenceDateMoscow(occ.start);
      if (map[d]) map[d].push(occ);
    }
    return map;
  }, [occurrences, days]);

  async function saveSettings(patch) {
    setMessage('');
    try {
      const res = await api.patch('/scheduler/teacher/settings/', patch);
      setSettings(res.data);
      setMessage('Настройки сохранены');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения настроек');
    }
  }

  async function saveSlot(e) {
    e.preventDefault();
    setError('');
    const payload = {
      weekday: Number(slotForm.weekday),
      start_time: slotForm.start_time,
      duration_minutes: Number(slotForm.duration_minutes),
      slot_type: slotForm.slot_type,
      max_students: slotForm.slot_type === 'group' ? Number(slotForm.max_students) || 4 : 1,
      note: slotForm.note,
    };
    const nextIds = slotForm.student_ids.map(String);
    const wasEdit = Boolean(editingSlotId);
    try {
      if (wasEdit) {
        await api.patch(`/scheduler/teacher/slots/${editingSlotId}/`, payload);
        const prev = new Set(
          (slots.find((s) => s.id === editingSlotId)?.students || []).map((s) => String(s.id))
        );
        const nextSet = new Set(nextIds);
        for (const id of nextIds) {
          if (!prev.has(id)) {
            await api.post(`/scheduler/teacher/slots/${editingSlotId}/enrollments/`, {
              student_id: Number(id),
            });
          }
        }
        for (const id of prev) {
          if (!nextSet.has(id)) {
            await api.delete(`/scheduler/teacher/slots/${editingSlotId}/enrollments/`, {
              data: { student_id: Number(id) },
            });
          }
        }
      } else {
        await api.post('/scheduler/teacher/slots/', {
          ...payload,
          student_ids: nextIds.map(Number),
        });
      }
      setSlotFormOpen(false);
      setEditingSlotId(null);
      setMessage(wasEdit ? 'Слот обновлён' : 'Слот добавлен');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить слот');
    }
  }

  function openCreateSlot() {
    setEditingSlotId(null);
    setSlotForm({
      weekday: 1,
      start_time: '16:00',
      duration_minutes: 60,
      slot_type: 'individual',
      max_students: 4,
      note: '',
      student_ids: [],
    });
    setSlotFormOpen(true);
  }

  function openEditSlot(slot) {
    setEditingSlotId(slot.id);
    setSlotForm({
      weekday: slot.weekday,
      start_time: (slot.start_time || '16:00').slice(0, 5),
      duration_minutes: slot.duration_minutes || 60,
      slot_type: slot.slot_type || 'individual',
      max_students: slot.max_students || 4,
      note: slot.note || '',
      student_ids: (slot.students || []).map((s) => String(s.id)),
    });
    setSlotFormOpen(true);
  }

  async function deleteSlot(id) {
    if (!window.confirm('Удалить недельный слот?')) return;
    await api.delete(`/scheduler/teacher/slots/${id}/`);
    reload();
  }

  async function cancelOccurrence(occ, studentId = null) {
    if (!occ.weekly_slot_id || !occ.original_date) {
      if (occ.oneoff_id) {
        await api.patch(`/scheduler/teacher/oneoffs/${occ.oneoff_id}/`, { is_cancelled: true });
        setSelectedOcc(null);
        reload();
        return;
      }
      setError('Нельзя отменить это вхождение');
      return;
    }
    try {
      await api.post('/scheduler/teacher/exceptions/', {
        weekly_slot_id: occ.weekly_slot_id,
        original_date: occ.original_date,
        kind: 'cancel',
        student_id: studentId || null,
      });
      setSelectedOcc(null);
      setMessage('Занятие отменено');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка отмены');
    }
  }

  async function rescheduleOccurrence(occ) {
    if (!occ.weekly_slot_id || !occ.original_date || !rescheduleAt) {
      setError('Укажите новое время');
      return;
    }
    try {
      await api.post('/scheduler/teacher/exceptions/', {
        weekly_slot_id: occ.weekly_slot_id,
        original_date: occ.original_date,
        kind: 'reschedule',
        student_id: cancelStudentOnly || null,
        new_start: rescheduleAt.length === 16 ? `${rescheduleAt}:00` : rescheduleAt,
      });
      setSelectedOcc(null);
      setRescheduleAt('');
      setCancelStudentOnly('');
      setMessage('Занятие перенесено');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка переноса');
    }
  }

  async function createOneoff(e) {
    e.preventDefault();
    try {
      const local = oneoffForm.start_local;
      const iso = local.length === 16 ? `${local}:00` : local;
      await api.post('/scheduler/teacher/oneoffs/', {
        start_time: iso,
        duration_minutes: Number(oneoffForm.duration_minutes),
        slot_type: oneoffForm.slot_type,
        student_ids: oneoffForm.student_ids.map(Number),
        note: oneoffForm.note,
      });
      setOneoffOpen(false);
      setMessage('Разовое занятие создано');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось создать разовое');
    }
  }

  async function addHolidays(e) {
    e.preventDefault();
    const dates = holidayInput
      .split(/[\s,;]+/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (!dates.length) return;
    try {
      const res = await api.post('/scheduler/teacher/holidays/', {
        dates,
        name: holidayName,
      });
      setHolidayHits(res.data.lesson_hits || []);
      setHolidayInput('');
      setMessage('Праздники добавлены');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка праздников');
    }
  }

  async function removeHoliday(id) {
    await api.delete(`/scheduler/teacher/holidays/${id}/`);
    reload();
  }

  async function saveExam(e) {
    e.preventDefault();
    if (!examStudentId || !examDate) return;
    try {
      const res = await api.put('/scheduler/teacher/exam-dates/', {
        student_id: Number(examStudentId),
        exam_date: examDate,
        label: 'Экзамен',
      });
      setExamCount(res.data.count);
      setMessage('Дата экзамена сохранена');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка даты экзамена');
    }
  }

  async function loadExamCount(studentId) {
    if (!studentId) {
      setExamCount(null);
      return;
    }
    try {
      const res = await api.get(`/scheduler/teacher/exam-count/${studentId}/`);
      setExamCount(res.data);
      if (res.data.exam_date) setExamDate(res.data.exam_date);
    } catch {
      setExamCount(null);
    }
  }

  function slotStyle(slot) {
    const startMin = minutesFromMidnight((slot.start_time || '00:00').slice(0, 5));
    const top = ((startMin - gridStartMin) / 60) * HOUR_HEIGHT;
    const height = Math.max((slot.duration_minutes / 60) * HOUR_HEIGHT, 28);
    return { top: `${top}px`, height: `${height}px` };
  }

  function slotClass(slot) {
    const parts = ['sched-occ'];
    if (slot.slot_type === 'group' || slot.is_group) parts.push('sched-occ--group');
    if (highlightStudentId) {
      const hit = (slot.students || []).some((s) => String(s.id) === String(highlightStudentId));
      parts.push(hit ? 'sched-occ--highlight' : 'sched-occ--dim');
    }
    return parts.join(' ');
  }

  const slotsByWeekday = useMemo(() => {
    const map = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    for (const slot of slots) {
      if (slot.is_active === false) continue;
      map[slot.weekday]?.push(slot);
    }
    return map;
  }, [slots]);

  function occStyle(occ) {
    const startMin = minutesFromMidnight(timeFromOccurrenceStart(occ.start));
    const top = ((startMin - gridStartMin) / 60) * HOUR_HEIGHT;
    const height = Math.max((occ.duration_minutes / 60) * HOUR_HEIGHT, 28);
    return { top: `${top}px`, height: `${height}px` };
  }

  function occClass(occ) {
    const parts = ['sched-occ'];
    if (occ.is_cancelled) parts.push('sched-occ--cancelled');
    if (occ.kind === 'rescheduled') parts.push('sched-occ--moved');
    if (occ.kind === 'oneoff') parts.push('sched-occ--oneoff');
    if (occ.is_group) parts.push('sched-occ--group');
    if (occ.on_holiday) parts.push('sched-occ--holiday');
    if (highlightStudentId) {
      const hit = (occ.students || []).some(
        (s) => String(s.id) === String(highlightStudentId) && !s.cancelled_for_student
      );
      parts.push(hit ? 'sched-occ--highlight' : 'sched-occ--dim');
    }
    return parts.join(' ');
  }

  const hourLabels = Array.from({ length: gridHours + 1 }, (_, i) => {
    const mins = gridStartMin + i * 60;
    const h = Math.floor(mins / 60);
    return `${String(h).padStart(2, '0')}:00`;
  });

  return (
    <div className="sched-page">
      <div className="page-header">
        <h1>Расписание</h1>
        <p className="page-subtitle">
          <strong>Расписание</strong> — постоянный шаблон («каждую среду в 19:00»).{' '}
          <strong>Календарь</strong> — конкретные даты: отмены, переносы и разовые.
          Журнал прошедших — в <Link to="/teacher">кабинете</Link>.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="sched-mode-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'template' ? ' is-active' : ''}`}
          aria-selected={viewMode === 'template'}
          onClick={() => setViewMode('template')}
        >
          Расписание (шаблон)
        </button>
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'calendar' ? ' is-active' : ''}`}
          aria-selected={viewMode === 'calendar'}
          onClick={() => setViewMode('calendar')}
        >
          Календарь (неделя)
        </button>
      </div>

      <div className="sched-toolbar">
        {viewMode === 'calendar' ? (
          <div className="sched-week-nav">
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setWeekStart(addDaysISO(weekStart, -7))}>
              ← Неделя
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setWeekStart(mondayOf(moscowDateISO()))}
            >
              Сегодня
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setWeekStart(addDaysISO(weekStart, 7))}>
              Неделя →
            </button>
            <span className="sched-week-label">
              {formatDateShort(weekStart)} — {formatDateShort(addDaysISO(weekStart, 6))}
            </span>
          </div>
        ) : (
          <p className="sched-toolbar-note muted">
            Постоянные слоты. Отмена на одну среду не освобождает среду в шаблоне — её по-прежнему видно здесь как занятую.
          </p>
        )}
        <div className="sched-toolbar-actions">
          <label className="sched-filter">
            Подсветить ученика
            <select
              value={highlightStudentId}
              onChange={(e) => setHighlightStudentId(e.target.value)}
            >
              <option value="">Все</option>
              {activeStudents.map((s) => (
                <option key={s.id} value={s.id}>
                  {studentLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreateSlot}>
            + Слот недели
          </button>
          {viewMode === 'calendar' && (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setOneoffOpen(true)}>
              + Разовое
            </button>
          )}
        </div>
      </div>

      {viewMode === 'calendar' && (
        <p className="sched-legend muted">
          <span className="sched-legend-item sched-legend-item--cancelled">зачёркнутое</span> — отмена или исходное место переноса{' '}
          <em>только на эту дату</em>. Постоянно поменять время — «Изменить» в шаблоне.
        </p>
      )}

      {loading ? (
        <p>Загрузка…</p>
      ) : viewMode === 'template' ? (
        <div className="sched-grid-wrap">
          <div className="sched-grid" style={{ '--sched-hour-h': `${HOUR_HEIGHT}px` }}>
            <div className="sched-grid-corner" />
            {WEEKDAY_NAMES.map((name) => (
              <div key={name} className="sched-day-head">
                <div className="sched-day-name">{name}</div>
                <div className="sched-day-date">каждую неделю</div>
              </div>
            ))}
            <div className="sched-hours">
              {hourLabels.map((label) => (
                <div key={label} className="sched-hour-label" style={{ height: HOUR_HEIGHT }}>
                  {label}
                </div>
              ))}
            </div>
            {[0, 1, 2, 3, 4, 5, 6].map((wd) => (
              <div
                key={wd}
                className="sched-day-col"
                style={{ height: gridHours * HOUR_HEIGHT }}
              >
                {hourLabels.slice(0, -1).map((label) => (
                  <div key={label} className="sched-hour-line" style={{ height: HOUR_HEIGHT }} />
                ))}
                {(slotsByWeekday[wd] || []).map((slot) => {
                  const names = (slot.students || []).map((s) => displayNameFor(s.id, s.name));
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      className={slotClass(slot)}
                      style={slotStyle(slot)}
                      onClick={() => openEditSlot(slot)}
                      title={names.join(', ') || slot.note || 'слот'}
                    >
                      <span className="sched-occ-people">
                        {names.length > 0
                          ? names.map((name) => (
                              <span key={name} className="sched-occ-person">
                                {name}
                              </span>
                            ))
                          : (
                              <span className="sched-occ-person sched-occ-person--empty">
                                {slot.slot_type === 'group' ? 'пусто' : slot.note || 'слот'}
                              </span>
                            )}
                      </span>
                      <span className="sched-occ-meta">
                        <span className="sched-occ-time">{(slot.start_time || '').slice(0, 5)}</span>
                        {slot.slot_type === 'group' && (
                          <span className="sched-occ-slots">
                            {slot.enrolled_count}/{slot.max_students}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="sched-grid-wrap">
          <div className="sched-grid" style={{ '--sched-hour-h': `${HOUR_HEIGHT}px` }}>
            <div className="sched-grid-corner" />
            {days.map((d, i) => (
              <div key={d} className="sched-day-head">
                <div className="sched-day-name">{WEEKDAY_NAMES[i]}</div>
                <div className="sched-day-date">{formatDateShort(d)}</div>
              </div>
            ))}
            <div className="sched-hours">
              {hourLabels.map((label) => (
                <div key={label} className="sched-hour-label" style={{ height: HOUR_HEIGHT }}>
                  {label}
                </div>
              ))}
            </div>
            {days.map((d) => (
              <div
                key={d}
                className="sched-day-col"
                style={{ height: gridHours * HOUR_HEIGHT }}
              >
                {hourLabels.slice(0, -1).map((label) => (
                  <div key={label} className="sched-hour-line" style={{ height: HOUR_HEIGHT }} />
                ))}
                {(occByDay[d] || []).map((occ) => {
                  const names = occActiveNames(occ);
                  return (
                    <button
                      key={occ.key}
                      type="button"
                      className={occClass(occ)}
                      style={occStyle(occ)}
                      onClick={() => {
                        setSelectedOcc(occ);
                        setRescheduleAt('');
                        setCancelStudentOnly('');
                      }}
                      title={occTitle(occ)}
                    >
                      <span className="sched-occ-people">
                        {names.length > 0
                          ? names.map((name) => (
                              <span key={name} className="sched-occ-person">
                                {name}
                              </span>
                            ))
                          : (
                              <span className="sched-occ-person sched-occ-person--empty">
                                {occ.is_group ? 'пусто' : occ.label || 'слот'}
                              </span>
                            )}
                      </span>
                      <span className="sched-occ-meta">
                        <span className="sched-occ-time">{formatTimeFromISO(occ.start)}</span>
                        {occ.is_group && (
                          <span className="sched-occ-slots">
                            {occ.enrolled_count}/{occ.max_students}
                          </span>
                        )}
                        {occ.is_cancelled && <span className="sched-occ-badge">отмена</span>}
                        {occ.kind === 'rescheduled' && !occ.is_cancelled && (
                          <span className="sched-occ-badge">перенос</span>
                        )}
                        {occ.on_holiday && <span className="sched-occ-badge">праздник</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="sched-panel">
        <h2>Шаблон недели</h2>
        <p className="muted sched-panel-hint">
          Имена в сетке — те же, что в кабинете («своё имя» ученика). Кнопка «Изменить» — день, время,
          длительность, пометка и состав группы.
        </p>
        {slots.length === 0 ? (
          <p className="muted">Пока нет слотов. Добавьте «каждый вторник в 16:00».</p>
        ) : (
          <ul className="sched-slot-list">
            {slots.map((slot) => (
              <li key={slot.id}>
                <div className="sched-slot-list-main">
                  <strong>{WEEKDAY_FULL[slot.weekday]}</strong> {slot.start_time}, {slot.duration_minutes} мин
                  {' — '}
                  {slot.slot_type === 'group' ? `группа ${slot.enrolled_count}/${slot.max_students}` : 'инд.'}
                  {': '}
                  {(slot.students || [])
                    .map((s) => displayNameFor(s.id, s.name))
                    .join(', ') || 'пусто'}
                  {slot.note ? ` (${slot.note})` : ''}
                </div>
                <div className="sched-slot-list-actions">
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => openEditSlot(slot)}>
                    Изменить
                  </button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => deleteSlot(slot.id)}>
                    Удалить
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="sched-panel sched-panel--grid2">
        <div>
          <h2>Рабочие часы и ссылки</h2>
          {settings && (
            <div className="sched-settings-form">
              <label>
                С
                <input
                  type="time"
                  value={settings.work_start?.slice(0, 5)}
                  onChange={(e) => setSettings({ ...settings, work_start: e.target.value })}
                />
              </label>
              <label>
                До
                <input
                  type="time"
                  value={settings.work_end?.slice(0, 5)}
                  onChange={(e) => setSettings({ ...settings, work_end: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() =>
                  saveSettings({
                    work_start: settings.work_start,
                    work_end: settings.work_end,
                    working_weekdays: settings.working_weekdays,
                  })
                }
              >
                Сохранить окно
              </button>
              <div className="sched-link-block">
                <div>
                  Публичная ссылка (без имён):
                  <br />
                  <a href={settings.public_url} target="_blank" rel="noreferrer">
                    {settings.public_url}
                  </a>
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => saveSettings({ rotate_public_token: true })}
                >
                  Сменить токен
                </button>
              </div>
              <div className="sched-link-block">
                <div>
                  Яндекс / iCal (полное расписание):
                  <br />
                  <code>{settings.ical_url}</code>
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => saveSettings({ rotate_ical_token: true })}
                >
                  Сменить iCal
                </button>
              </div>
            </div>
          )}
        </div>
        <div>
          <h2>Свободное время</h2>
          <pre className="sched-free-text">{freeText || '—'}</pre>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => navigator.clipboard?.writeText(freeText)}>
            Копировать
          </button>
        </div>
      </section>

      <section className="sched-panel sched-panel--grid2">
        <div>
          <h2>Праздники</h2>
          <form className="sched-inline-form" onSubmit={addHolidays}>
            <input
              type="text"
              placeholder="2026-01-01, 2026-01-07"
              value={holidayInput}
              onChange={(e) => setHolidayInput(e.target.value)}
            />
            <input
              type="text"
              placeholder="Название"
              value={holidayName}
              onChange={(e) => setHolidayName(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Добавить
            </button>
          </form>
          <ul className="sched-slot-list">
            {holidays.map((h) => (
              <li key={h.id}>
                {h.date} {h.name}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => removeHoliday(h.id)}>
                  Удалить
                </button>
              </li>
            ))}
          </ul>
          {holidayHits.length > 0 && (
            <div className="sched-hits">
              <strong>Занятия в эти дни:</strong>
              <ul>
                {holidayHits.map((hit) => (
                  <li key={hit.key}>
                    {formatTimeFromISO(hit.start)} — {hit.label}
                    {hit.is_cancelled ? ' (уже отменено)' : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div>
          <h2>Занятия до экзамена</h2>
          <form className="sched-inline-form" onSubmit={saveExam}>
            <select
              value={examStudentId}
              onChange={(e) => {
                setExamStudentId(e.target.value);
                loadExamCount(e.target.value);
              }}
            >
              <option value="">Ученик…</option>
                  {students
                    .filter((s) => !s.relation_status || s.relation_status === 'active')
                    .map((s) => (
                    <option key={s.id} value={s.id}>
                      {studentLabel(s)}
                    </option>
                  ))}
            </select>
            <input type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} />
            <button type="submit" className="btn btn-primary btn-sm">
              Сохранить
            </button>
          </form>
          {examCount && (
            <div className="sched-exam-count">
              {examCount.warning ? (
                <p>{examCount.warning}</p>
              ) : (
                <>
                  <p>
                    До {examCount.exam_date}: <strong>{examCount.count}</strong> занятий
                    (праздники не считаются).
                  </p>
                  {examCount.holiday_hits?.length > 0 && (
                    <p className="muted">
                      На праздники попадает: {examCount.holiday_hits.length} — перенесите или отмените.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {slotFormOpen && (
        <div
          className="sched-modal-backdrop"
          role="presentation"
          onClick={() => {
            setSlotFormOpen(false);
            setEditingSlotId(null);
          }}
        >
          <form
            className="sched-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveSlot}
          >
            <h3>{editingSlotId ? 'Изменить слот' : 'Недельный слот'}</h3>
            <label>
              День
              <select
                value={slotForm.weekday}
                onChange={(e) => setSlotForm({ ...slotForm, weekday: Number(e.target.value) })}
              >
                {WEEKDAY_FULL.map((name, i) => (
                  <option key={name} value={i}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Время
              <input
                type="time"
                value={slotForm.start_time}
                onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })}
                required
              />
            </label>
            <label>
              Минут
              <input
                type="number"
                min={15}
                max={480}
                value={slotForm.duration_minutes}
                onChange={(e) => setSlotForm({ ...slotForm, duration_minutes: e.target.value })}
              />
            </label>
            <label>
              Тип
              <select
                value={slotForm.slot_type}
                onChange={(e) =>
                  setSlotForm({
                    ...slotForm,
                    slot_type: e.target.value,
                    student_ids:
                      e.target.value === 'individual' && slotForm.student_ids.length > 1
                        ? slotForm.student_ids.slice(0, 1)
                        : slotForm.student_ids,
                  })
                }
              >
                <option value="individual">Индивидуальное</option>
                <option value="group">Группа (до 4)</option>
              </select>
            </label>
            <StudentPicker
              students={activeStudents}
              selectedIds={slotForm.student_ids}
              max={slotForm.slot_type === 'group' ? 4 : 1}
              onChange={(ids) => setSlotForm({ ...slotForm, student_ids: ids })}
            />
            <label>
              Пометка
              <input
                value={slotForm.note}
                onChange={(e) => setSlotForm({ ...slotForm, note: e.target.value })}
                placeholder="Например: ОГЭ / ЕГЭ"
              />
            </label>
            <div className="sched-modal-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setSlotFormOpen(false);
                  setEditingSlotId(null);
                }}
              >
                Отмена
              </button>
              <button type="submit" className="btn btn-primary">
                {editingSlotId ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      )}

      {oneoffOpen && (
        <div className="sched-modal-backdrop" role="presentation" onClick={() => setOneoffOpen(false)}>
          <form className="sched-modal" onClick={(e) => e.stopPropagation()} onSubmit={createOneoff}>
            <h3>Разовое занятие</h3>
            <label>
              Дата и время
              <input
                type="datetime-local"
                value={oneoffForm.start_local}
                onChange={(e) => setOneoffForm({ ...oneoffForm, start_local: e.target.value })}
                required
              />
            </label>
            <label>
              Минут
              <input
                type="number"
                value={oneoffForm.duration_minutes}
                onChange={(e) => setOneoffForm({ ...oneoffForm, duration_minutes: e.target.value })}
              />
            </label>
            <label>
              Тип
              <select
                value={oneoffForm.slot_type}
                onChange={(e) =>
                  setOneoffForm({
                    ...oneoffForm,
                    slot_type: e.target.value,
                    student_ids:
                      e.target.value === 'individual' && oneoffForm.student_ids.length > 1
                        ? oneoffForm.student_ids.slice(0, 1)
                        : oneoffForm.student_ids,
                  })
                }
              >
                <option value="individual">Индивидуальное</option>
                <option value="group">Группа</option>
              </select>
            </label>
            <StudentPicker
              students={activeStudents}
              selectedIds={oneoffForm.student_ids}
              max={oneoffForm.slot_type === 'group' ? 4 : 1}
              onChange={(ids) => setOneoffForm({ ...oneoffForm, student_ids: ids })}
            />
            <div className="sched-modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setOneoffOpen(false)}>
                Отмена
              </button>
              <button type="submit" className="btn btn-primary">
                Создать
              </button>
            </div>
          </form>
        </div>
      )}

      {selectedOcc && (
        <div className="sched-modal-backdrop" role="presentation" onClick={() => setSelectedOcc(null)}>
          <div className="sched-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <h3>{occTitle(selectedOcc)}</h3>
            <p>
              {formatTimeFromISO(selectedOcc.start)} · {selectedOcc.duration_minutes} мин
              {selectedOcc.is_cancelled ? ' · отменено' : ''}
              {selectedOcc.on_holiday ? ` · ${selectedOcc.holiday_name || 'праздник'}` : ''}
            </p>
            {selectedOcc.students?.length > 0 && (
              <ul className="sched-occ-student-list">
                {selectedOcc.students.map((s) => (
                  <li key={s.id}>
                    {displayNameFor(s.id, s.name)}
                    {s.cancelled_for_student ? ' (отмена для ученика)' : ''}
                  </li>
                ))}
              </ul>
            )}
            {selectedOcc.weekly_slot_id && (
              <div className="sched-modal-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const slot = slots.find((s) => s.id === selectedOcc.weekly_slot_id);
                    if (!slot) {
                      setError('Слот шаблона не найден — обновите страницу');
                      return;
                    }
                    setSelectedOcc(null);
                    openEditSlot(slot);
                  }}
                >
                  Изменить слот…
                </button>
              </div>
            )}
            {!selectedOcc.is_cancelled && (
              <>
                <p className="muted">
                  Отмена и перенос действуют <strong>только на эту дату</strong>. Шаблон («каждую неделю») не меняется —
                  для постоянного сдвига времени откройте «Изменить слот».
                </p>
                <div className="sched-modal-actions">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => cancelOccurrence(selectedOcc)}
                  >
                    Отменить только эту дату
                  </button>
                </div>
                {selectedOcc.is_group && selectedOcc.weekly_slot_id && (
                  <label>
                    Отменить только ученика
                    <select
                      value={cancelStudentOnly}
                      onChange={(e) => setCancelStudentOnly(e.target.value)}
                    >
                      <option value="">—</option>
                      {(selectedOcc.students || [])
                        .filter((s) => !s.cancelled_for_student)
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={!cancelStudentOnly}
                      onClick={() => cancelOccurrence(selectedOcc, cancelStudentOnly)}
                    >
                      Отменить ученика
                    </button>
                  </label>
                )}
                {selectedOcc.weekly_slot_id && (
                  <label>
                    Перенести на
                    <input
                      type="datetime-local"
                      value={rescheduleAt}
                      onChange={(e) => setRescheduleAt(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => rescheduleOccurrence(selectedOcc)}
                    >
                      Перенести только эту дату
                    </button>
                  </label>
                )}
              </>
            )}
            <div className="sched-modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setSelectedOcc(null)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
